import { formatLocDateMMDDYYYY } from '@mummssoftware/utils/formatters';
import {
  formatAttributeIdsParam,
  formatAttributeMatchModeParam,
} from '@mummssoftware/utils/pimReader';
import { getHeaders } from '@mummssoftware/utils/utils';
import { Bereavement } from '@mummssoftware/utils/roleData';
import { ComprehensiveAssessment } from '@mummssoftware/utils/constants';
import {
  ATTRIBUTE_FILTER_ALL,
  BREV,
  CompAss,
  PM,
  PostMortem,
  SUP,
  Supervisory,
} from './constants';
import { total } from '../../translations/en.json';

export type VisitAttributeFilterOption = {
  id: number;
  name: string;
  shortname: string;
};

export type SummaryAttributeMatchMode = 'AND' | 'OR';

export const SUMMARY_ATTRIBUTE_MATCH_AND: SummaryAttributeMatchMode = 'AND';
export const SUMMARY_ATTRIBUTE_MATCH_OR: SummaryAttributeMatchMode = 'OR';

const sortByName = (
  a: VisitAttributeFilterOption,
  b: VisitAttributeFilterOption,
) => {
  if (a.name < b.name) return -1;
  if (a.name > b.name) return 1;
  return 0;
};

export const visitAttributesToFilterOptions = (
  visitAttributes: mumms.VisitAttribute[] | null | undefined,
): VisitAttributeFilterOption[] =>
  (visitAttributes || [])
    .filter(
      (attribute): attribute is mumms.VisitAttribute & { id: number } =>
        !!attribute?.id,
    )
    .map(({ id, name, shortname }) => ({
      id,
      name: name || '',
      shortname: shortname || '',
    }))
    .sort(sortByName);

const attributeNameForId = (
  attributeId: number,
  options: VisitAttributeFilterOption[],
) => options.find((option) => option.id === attributeId)?.name;

export const attributeNamesForIds = (
  attributeIds: number[],
  options: VisitAttributeFilterOption[],
) =>
  attributeIds
    .map((attributeId) => attributeNameForId(attributeId, options))
    .filter(Boolean) as string[];

export const HOPE_ATTRIBUTE_PREFIX = 'HOPE';

export const isHopeAttributeName = (name: string) =>
  name.startsWith(HOPE_ATTRIBUTE_PREFIX);

export const partitionAttributeFilterOptions = (
  options: VisitAttributeFilterOption[],
) =>
  options.reduce<{
    hopeAttributes: VisitAttributeFilterOption[];
    otherAttributes: VisitAttributeFilterOption[];
  }>(
    (groups, option) => {
      if (isHopeAttributeName(option.name)) {
        groups.hopeAttributes.push(option);
      } else {
        groups.otherAttributes.push(option);
      }
      return groups;
    },
    { hopeAttributes: [], otherAttributes: [] },
  );

export const isAttributeFilterAllSelected = (
  attributeIds: number[] | null | undefined,
) => !attributeIds?.length;

export const toggleAttributeFilterId = (
  attributeIds: number[] | null | undefined,
  attributeId: number,
): number[] => {
  const current = attributeIds || [];
  return current.includes(attributeId)
    ? current.filter((id) => id !== attributeId)
    : [...current, attributeId];
};

export const attributeFilterSelectValue = (attributeIds: number[] | null) =>
  attributeIds?.length ? attributeIds : [ATTRIBUTE_FILTER_ALL];

export const attributeIdsFromFilterSelect = (value: number[]) => {
  if (value[value.length - 1] === ATTRIBUTE_FILTER_ALL) {
    return [];
  }
  return value.filter((id) => id !== ATTRIBUTE_FILTER_ALL);
};

export const fetchPimreaderVisitsCrossPatient = async (
  keycloak: any,
  agency: string,
  siteId: string | null | undefined,
  fromDate: any,
  toDate: any,
  minDurationHH: string | null,
  minDurationMM: string | null,
  chartAfterDD: string | null,
  chartAfterHH: string | null,
  attributeIds: number[] | null | undefined,
  attributeMatchMode?: 'AND' | 'OR' | null,
) => {
  let minDuration = (minDurationHH && Number.parseInt(minDurationHH) * 60) || 0;
  if (minDurationMM) {
    minDuration += Number.parseInt(minDurationMM);
  }
  let chartAfter =
    (chartAfterDD && Number.parseInt(chartAfterDD) * 24 * 60) || 0;
  if (chartAfterHH) {
    chartAfter += Number.parseInt(chartAfterHH) * 60;
  }
  const headers = await getHeaders(keycloak);
  headers.set('Authorization', `Bearer ${keycloak.token}`);
  headers.set('Accept-Profile', 'pimreader');

  let authProxyUrl = `${process.env.REACT_APP_AUTH_PROXY_URL}`;
  authProxyUrl += `/api/query/rpc/getvisitsummarybyperson?agency=${agency}`;
  authProxyUrl += `&p_site_id=${siteId}`;
  authProxyUrl += `&p_fromdate=${formatLocDateMMDDYYYY(fromDate)}`;
  authProxyUrl += `&p_todate=${formatLocDateMMDDYYYY(toDate)}`;
  authProxyUrl += `&p_visitstatus=`;
  authProxyUrl += `&p_mindurationmins=${minDuration}`;
  authProxyUrl += `&p_minchartingmins=${chartAfter}`;
  authProxyUrl += `&p_attribute_ids=${formatAttributeIdsParam(attributeIds)}`;
  authProxyUrl += `&p_attribute_match_mode=${formatAttributeMatchModeParam(
    attributeMatchMode,
  )}`;

  const response = (await fetch(authProxyUrl, {
    headers,
    credentials: 'same-origin',
  })) as Response;

  // A 504 is a Gateway Timeout Error
  if (response.status === 200) {
    const priorVisits = await response.json();
    return priorVisits.sort(
      (valueA: any, valueB: any) =>
        new Date(valueB.effectivedatetime).getTime() -
        new Date(valueA.effectivedatetime).getTime(),
    );
  }
  return null;
};

/*
  OP-31446: need to abbreviate headers for long attribute names
*/
export const getAttributeDisplayName = (initialName: string) => {
  let displayName;
  switch (initialName) {
    case Bereavement:
      displayName = BREV;
      break;
    case ComprehensiveAssessment:
      displayName = CompAss;
      break;
    case PostMortem:
      displayName = PM;
      break;
    case Supervisory:
      displayName = SUP;
      break;
    default:
      displayName = initialName;
      break;
  }
  return displayName;
};

export const isOfficeFilterActive = (
  crossPatientVisits: mumms.VisitSummaryDTO[],
) => {
  let isActive = true;
  crossPatientVisits.forEach((provider) => {
    if (provider.siteoffice === total) {
      isActive = false;
    }
  });
  return isActive;
};

export const pinTotalRow = (gridAPI: any, visits: any) => {
  if (gridAPI) {
    const lastRow = visits && visits[visits.length - 1];
    lastRow && gridAPI.setPinnedBottomRowData([lastRow]);
  }
};
